import { motion } from "framer-motion";
import { Phone, Check, Sparkles } from "lucide-react";
import { GearCard } from "./GearCard";
import { CONTACT } from "../data/gear";

const HERO_INITIAL = { opacity: 0, y: 24 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.6 };
const SECTION_VIEWPORT = { once: true, margin: "-80px" };
const FEATURE_INITIAL = { opacity: 0, y: 30 };
const FEATURE_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const FEATURE_TRANSITION = { duration: 0.6 };

export const GearPage = ({
  testid, hero, eyebrow, title, intro, banner, gear, onQuote, featurePackages = [],
}) => {
  return (
    <div data-testid={testid}>
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end">
        <img src={hero} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-14">
          <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-accent mb-4">{eyebrow}</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white tracking-tight">{title}</h1>
            <p className="mt-4 text-lg text-white/85 max-w-xl leading-relaxed">{intro}</p>
          </motion.div>
        </div>
      </section>

      {/* BANNER */}
      <div className="bg-accent text-accent-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <p className="font-semibold tracking-wide">{banner}</p>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 text-sm underline underline-offset-4">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </div>
      </div>

      {/* FEATURED PACKAGES */}
      {featurePackages.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 space-y-8">
          {featurePackages.map((pkg, i) => (
            <FeaturePackage key={pkg.name} pkg={pkg} imageRight={i % 2 === 1} onQuote={onQuote} />
          ))}
        </section>
      )}

      {/* GEAR GRID */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {gear.map((item, i) => (
            <GearCard key={item.name} item={item} index={i} onQuote={onQuote} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <div className="max-w-6xl mx-auto rounded-3xl bg-primary text-primary-foreground px-8 sm:px-16 py-14 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Build your custom package</h2>
          <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">
            Mix and match any gear above. We'll deliver, set everything up, and tear it all down when you're done.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="page-quote-btn"
              onClick={() => onQuote()}
              className="px-8 py-4 rounded-full bg-accent text-accent-foreground font-semibold uppercase tracking-widest text-sm hover:bg-accent/90 transition-colors"
            >
              Request a Quote
            </button>
            <a href={CONTACT.phoneHref} className="px-8 py-4 rounded-full border border-white/30 font-semibold uppercase tracking-widest text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" /> Call to Book
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const FeaturePackage = ({ pkg, imageRight, onQuote }) => {
  const slug = pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const image = (
    <div className={`relative min-h-[300px] lg:min-h-full ${pkg.fit === "contain" ? "bg-secondary/30 flex items-center justify-center p-4 lg:p-6" : ""}`}>
      {pkg.fit === "contain" ? (
        <img src={pkg.image} alt={pkg.name} loading="lazy" decoding="async" className="w-full max-h-[460px] object-contain rounded-xl" />
      ) : (
        <img src={pkg.image} alt={pkg.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      )}
    </div>
  );
  const details = (
    <div className="p-8 sm:p-12">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-widest">
        <Sparkles className="w-3 h-3" /> Featured Package
      </span>
      <h2 className="mt-5 font-heading text-4xl font-bold text-primary tracking-tight">{pkg.name}</h2>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-bold text-accent">{pkg.price}</span>
        <span className="text-sm text-muted-foreground">{pkg.priceUnit || "for 1 day"}</span>
      </div>
      {pkg.blurb && (
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{pkg.blurb}</p>
      )}
      <ul className="mt-6 space-y-2.5">
        {pkg.items.map((it) => (
          <li key={it} className="flex gap-2.5 text-sm text-foreground">
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {pkg.addon && (
        <p className="mt-5 rounded-xl bg-secondary/60 p-3 text-sm text-foreground">{pkg.addon}</p>
      )}
      <button
        data-testid={`feature-package-quote-${slug}`}
        onClick={() => onQuote(pkg.name)}
        className="mt-7 w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors"
      >
        Request this Package
      </button>
    </div>
  );
  return (
    <motion.div
      data-testid={`feature-package-${slug}`}
      initial={FEATURE_INITIAL}
      whileInView={FEATURE_WHILE_IN_VIEW}
      viewport={SECTION_VIEWPORT}
      transition={FEATURE_TRANSITION}
      className="grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-border/60 bg-card shadow-sm"
    >
      {imageRight ? (
        <>
          <div className="order-2 lg:order-1">{details}</div>
          <div className="order-1 lg:order-2">{image}</div>
        </>
      ) : (
        <>
          {image}
          {details}
        </>
      )}
    </motion.div>
  );
};

